import { createClient } from '@/lib/supabase/client';

export interface AppUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  passwordHash: string;
  role: 'admin' | 'field';
  status: 'active' | 'inactive' | 'deactivated';
  assignedSites: string[];
  permissions: {
    scanAttendance: boolean;
    viewWorkers: boolean;
    viewPayments: boolean;
  };
  createdAt: string;
  lastLogin: string | null;
}

function toAppUser(row: Record<string, unknown>): AppUser {
  return {
    id: row.id as string,
    firstName: row.first_name as string,
    lastName: row.last_name as string,
    email: row.email as string,
    phone: row.phone as string,
    passwordHash: row.password_hash as string,
    role: row.role as 'admin' | 'field',
    status: row.status as 'active' | 'inactive' | 'deactivated',
    assignedSites: (row.assigned_sites as string[]) ?? [],
    permissions: (row.permissions as AppUser['permissions']) ?? {
      scanAttendance: true,
      viewWorkers: false,
      viewPayments: false,
    },
    createdAt: row.created_at as string,
    lastLogin: row.last_login as string | null,
  };
}

export async function getAllAppUsers(): Promise<AppUser[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('app_users')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map(toAppUser);
}

export async function getAppUserByEmail(email: string): Promise<AppUser | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('app_users')
    .select('*')
    .eq('email', email.toLowerCase())
    .single();
  if (error) return null;
  return toAppUser(data);
}

export async function createAppUser(params: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  role: 'admin' | 'field';
  assignedSites: string[];
  permissions: { scanAttendance: boolean; viewWorkers: boolean; viewPayments: boolean };
}): Promise<AppUser> {
  const supabase = createClient();
  const id = `user-${Date.now()}`;
  const { data, error } = await supabase
    .from('app_users')
    .insert({
      id,
      first_name: params.firstName,
      last_name: params.lastName,
      email: params.email.toLowerCase(),
      phone: params.phone,
      password_hash: params.password,
      role: params.role,
      status: 'active',
      assigned_sites: params.assignedSites,
      permissions: params.permissions,
    })
    .select()
    .single();
  if (error) throw error;
  return toAppUser(data);
}

export async function updateAppUser(
  id: string,
  params: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    role?: 'admin' | 'field';
    status?: 'active' | 'inactive' | 'deactivated';
    assignedSites?: string[];
    permissions?: { scanAttendance: boolean; viewWorkers: boolean; viewPayments: boolean };
  }
): Promise<void> {
  const supabase = createClient();
  const updates: Record<string, unknown> = {};
  if (params.firstName !== undefined) updates.first_name = params.firstName;
  if (params.lastName !== undefined) updates.last_name = params.lastName;
  if (params.email !== undefined) updates.email = params.email.toLowerCase();
  if (params.phone !== undefined) updates.phone = params.phone;
  if (params.role !== undefined) updates.role = params.role;
  if (params.status !== undefined) updates.status = params.status;
  if (params.assignedSites !== undefined) updates.assigned_sites = params.assignedSites;
  if (params.permissions !== undefined) updates.permissions = params.permissions;

  const { error } = await supabase.from('app_users').update(updates).eq('id', id);
  if (error) throw error;
}

export async function deleteAppUser(id: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase.from('app_users').delete().eq('id', id);
  if (error) throw error;
}

export async function updateLastLogin(id: string): Promise<void> {
  const supabase = createClient();
  await supabase
    .from('app_users')
    .update({ last_login: new Date().toISOString() })
    .eq('id', id);
}
